import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

export interface Option<T extends string> {
  value: T;
  label: string;
  /** What the field shows once it is chosen, where the whole label has no room: `JPY`. */
  short?: string;
  /** Its mark before its words: a bus for `bus`. */
  icon?: ReactNode;
  /** What it means, said under it in the list: `Resting and the sights`. */
  hint?: string;
}

export interface PickProps<T extends string> {
  label: string;
  options: readonly Option<T>[];
  value: T | null;
  /** Always a real choice: only a clearable field can be emptied, and that calls `onClear`. */
  onChange: (value: T) => void;
  /** Makes the field clearable (× button, Backspace). */
  onClear?: () => void;
  /** What it shows while nothing is chosen: words, or a mark and words. */
  placeholder?: ReactNode;
  searchable?: boolean;
  /** It must be filled in: its label is marked with a star. */
  required?: boolean;
  /** Its label for a screen reader only, where the row it is in says whose it is. */
  quiet?: boolean;
  /** Drawn as the end of the field before it: a currency after an amount. */
  suffix?: boolean;
}

/**
 * One choice from a list, in place of `<select>` (docs/02 §6): typed to search, arrows to move,
 * Enter to choose, every word it says to a screen reader in the UI language.
 */
export function Pick<T extends string>({
  label,
  options,
  value,
  onChange,
  onClear,
  placeholder,
  searchable = true,
  required = false,
  quiet = false,
  suffix = false,
}: PickProps<T>) {
  const { t } = useTranslation();
  const id = useId();
  const selected = options.find((option) => option.value === value) ?? null;
  // The list opens over everything: in a dialog, in the dialog's own layer, which the page's
  // body is under; else on the body. It is not cut off by a dialog that scrolls.
  const [field, setField] = useState<HTMLDivElement | null>(null);
  const host =
    field?.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ?? document.body;

  return (
    <div className={suffix ? 'pick-suffix' : 'field'} ref={setField}>
      <label
        className={quiet ? 'visually-hidden' : 'field-label'}
        htmlFor={id}
        data-required={required || undefined}
      >
        {label}
      </label>
      <Select<Option<T>>
        inputId={id}
        className="pick"
        classNamePrefix="pick"
        formatOptionLabel={(option, { context }) => {
          const said = context === 'value' ? (option.short ?? option.label) : option.label;
          const words =
            context === 'menu' && option.hint !== undefined ? (
              <span className="pick-hinted">
                {said}
                <span className="pick-hint">{option.hint}</span>
              </span>
            ) : (
              said
            );
          return option.icon ? (
            <span className="pick-icon-label">
              {option.icon}
              {words}
            </span>
          ) : (
            words
          );
        }}
        unstyled
        options={options}
        value={selected}
        isSearchable={searchable}
        isClearable={onClear !== undefined}
        // Upwards when there is no room below, as at the foot of a dialog that scrolls.
        menuPlacement="auto"
        menuPortalTarget={host}
        menuPosition="fixed"
        // Above the widgets on the map: react-select writes its own layer into the style.
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 60 }),
          // At the end of a field the picker is narrow; its list has room for whole names.
          menu: (base) => (suffix ? { ...base, minWidth: 260, right: 0, left: 'auto' } : base),
        }}
        // Focus stays on the field after a choice, as the combobox pattern expects, so the
        // keyboard user goes on from where they were and the screen reader hears "chosen".
        blurInputOnSelect={false}
        placeholder={placeholder ?? t('pick.placeholder')}
        noOptionsMessage={() => t('pick.noOptions')}
        screenReaderStatus={({ count }) => t('pick.count', { count })}
        ariaLiveMessages={{
          guidance: () => t('pick.guidance'),
          onChange: ({ label: chosen }) => t('pick.chosen', { label: chosen }),
          onFocus: ({ label: focused }) => t('pick.focused', { label: focused }),
          onFilter: ({ inputValue, resultsMessage }) =>
            inputValue === ''
              ? resultsMessage
              : t('pick.filtered', { results: resultsMessage, text: inputValue }),
        }}
        onChange={(option) => {
          if (option === null) {
            onClear?.();
          } else {
            onChange(option.value);
          }
        }}
      />
    </div>
  );
}
